import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Folder, GripVertical, Video } from 'lucide-react';
import type { HandbookTrainingNode } from '../../../types/handbookTraining';
import type { HandbookTrainingTreeNode } from '../../../utils/handbookTrainingTree';
import { handbookTrainingSearchMatch } from '../../../utils/handbookTrainingTree';
import { isHandbookTrainingVideoNew } from '../../../utils/handbookTrainingAccess';

const DRAG_MIME = 'application/x-lctf-handbook-training-node';

export type HandbookTrainingDropInsertHint = { targetId: string; before: boolean };

type Props = {
  tree: HandbookTrainingTreeNode[];
  flatNodes?: HandbookTrainingNode[];
  watchedNodeIds: ReadonlySet<string>;
  selectedId: string | null;
  searchQuery: string;
  canManage?: boolean;
  onSelect: (nodeId: string, kind: 'folder' | 'video') => void;
  onMoveNode?: (nodeId: string, newParentId: string | null) => void;
  onInsertSibling?: (draggedId: string, targetId: string, insertBefore: boolean) => void;
};

function VideoStatusBadges({
  node,
  watchedNodeIds,
}: {
  node: HandbookTrainingNode;
  watchedNodeIds: ReadonlySet<string>;
}) {
  const isNew = isHandbookTrainingVideoNew(node, watchedNodeIds);
  return (
    <>
      {isNew && <span className="people-handbook-training-tree-new">New</span>}
      {!node.published && <span className="people-handbook-training-tree-draft">Draft</span>}
    </>
  );
}

function subtreeContainsSelection(node: HandbookTrainingTreeNode, selectedId: string | null): boolean {
  if (!selectedId) return false;
  if (node.id === selectedId) return true;
  return node.children.some(c => subtreeContainsSelection(c, selectedId));
}

function readDragNodeId(e: React.DragEvent): string | null {
  const id = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData('text/plain');
  return id.trim() || null;
}

function insertBeforeFromEvent(e: React.DragEvent): boolean {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2;
}

function TreeBranch({
  node,
  watchedNodeIds,
  selectedId,
  searchQuery,
  canManage,
  onSelect,
  onMoveNode,
  onInsertSibling,
  defaultOpen,
  dragOverFolderId,
  setDragOverFolderId,
  dropInsertHint,
  setDropInsertHint,
}: {
  node: HandbookTrainingTreeNode;
  watchedNodeIds: ReadonlySet<string>;
  selectedId: string | null;
  searchQuery: string;
  canManage: boolean;
  onSelect: Props['onSelect'];
  onMoveNode?: Props['onMoveNode'];
  onInsertSibling?: Props['onInsertSibling'];
  defaultOpen: boolean;
  dragOverFolderId: string | null;
  setDragOverFolderId: (id: string | null) => void;
  dropInsertHint: HandbookTrainingDropInsertHint | null;
  setDropInsertHint: (hint: HandbookTrainingDropInsertHint | null) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const isFolder = node.kind === 'folder';
  const selected = selectedId === node.id;
  const q = searchQuery.trim();
  const selfMatch = !q || handbookTrainingSearchMatch(node, q);
  const childMatch = isFolder && node.children.some(c => treeNodeMatchesSearch(c, q));

  if (q && !selfMatch && !childMatch) return null;

  const startDrag = (e: React.DragEvent) => {
    if (!canManage) return;
    e.dataTransfer.setData(DRAG_MIME, node.id);
    e.dataTransfer.setData('text/plain', node.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onReorderDragOver = (e: React.DragEvent) => {
    if (!canManage || !onInsertSibling) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDropInsertHint({ targetId: node.id, before: insertBeforeFromEvent(e) });
  };

  const onReorderDrop = (e: React.DragEvent) => {
    if (!canManage || !onInsertSibling) return;
    e.preventDefault();
    e.stopPropagation();
    const draggedId = readDragNodeId(e);
    setDropInsertHint(null);
    if (!draggedId || draggedId === node.id) return;
    onInsertSibling(draggedId, node.id, insertBeforeFromEvent(e));
  };

  const onFolderDragOver = (e: React.DragEvent) => {
    if (!canManage || !onMoveNode) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const nestInto = e.clientY >= rect.top + rect.height * 0.35;
    if (nestInto) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverFolderId(node.id);
      setDropInsertHint(null);
    } else if (onInsertSibling) {
      onReorderDragOver(e);
    }
  };

  const onFolderDrop = (e: React.DragEvent) => {
    if (!canManage) return;
    e.preventDefault();
    e.stopPropagation();
    const draggedId = readDragNodeId(e);
    if (!draggedId || draggedId === node.id) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const nestInto = e.clientY >= rect.top + rect.height * 0.35;
    setDragOverFolderId(null);
    setDropInsertHint(null);
    if (nestInto && onMoveNode) {
      onMoveNode(draggedId, node.id);
    } else if (onInsertSibling) {
      onInsertSibling(draggedId, node.id, insertBeforeFromEvent(e));
    }
  };

  const showInsertBefore =
    dropInsertHint?.targetId === node.id && dropInsertHint.before === true;
  const showInsertAfter =
    dropInsertHint?.targetId === node.id && dropInsertHint.before === false;

  if (node.kind === 'video') {
    return (
      <div
        className={`people-handbook-training-tree-row ${selected ? 'selected' : ''}`}
        onDragOver={onReorderDragOver}
        onDragLeave={() => setDropInsertHint(null)}
        onDrop={onReorderDrop}
      >
        {showInsertBefore && <div className="people-handbook-training-tree-insert-line before" />}
        {canManage && (
          <span
            className="people-handbook-training-tree-drag"
            draggable
            onDragStart={startDrag}
            title="Drag to reorder or move"
            aria-label={`Drag ${node.title}`}
          >
            <GripVertical size={14} aria-hidden />
          </span>
        )}
        <button
          type="button"
          className={`people-handbook-training-tree-item video ${selected ? 'active' : ''}`}
          onClick={() => onSelect(node.id, 'video')}
        >
          <Video size={14} aria-hidden className="people-handbook-training-tree-icon" />
          <span className="people-handbook-training-tree-label">
            {node.title}
            <VideoStatusBadges node={node} watchedNodeIds={watchedNodeIds} />
          </span>
        </button>
        {showInsertAfter && <div className="people-handbook-training-tree-insert-line after" />}
      </div>
    );
  }

  const folderDropActive = dragOverFolderId === node.id;

  return (
    <div className="people-handbook-training-tree-folder">
      <div
        className={`people-handbook-training-tree-folder-row ${folderDropActive ? 'drop-target' : ''}`}
        onDragOver={onFolderDragOver}
        onDragLeave={() => {
          setDragOverFolderId(null);
          setDropInsertHint(null);
        }}
        onDrop={onFolderDrop}
      >
        {showInsertBefore && <div className="people-handbook-training-tree-insert-line before" />}
        {canManage && (
          <span
            className="people-handbook-training-tree-drag"
            draggable
            onDragStart={startDrag}
            title="Drag to reorder or move"
            aria-label={`Drag ${node.title}`}
          >
            <GripVertical size={14} aria-hidden />
          </span>
        )}
        <button
          type="button"
          className="people-handbook-training-tree-chevron"
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <button
          type="button"
          className={`people-handbook-training-tree-item folder ${selected ? 'active' : ''}`}
          onClick={() => onSelect(node.id, 'folder')}
        >
          <Folder size={14} aria-hidden className="people-handbook-training-tree-icon" />
          <span>{node.title}</span>
        </button>
        {showInsertAfter && <div className="people-handbook-training-tree-insert-line after" />}
      </div>
      {open && node.children.length > 0 && (
        <div className="people-handbook-training-tree-children">
          {node.children.map(child => (
            <TreeBranch
              key={child.id}
              node={child}
              watchedNodeIds={watchedNodeIds}
              selectedId={selectedId}
              searchQuery={searchQuery}
              canManage={canManage}
              onSelect={onSelect}
              onMoveNode={onMoveNode}
              onInsertSibling={onInsertSibling}
              defaultOpen={!!q || subtreeContainsSelection(child, selectedId)}
              dragOverFolderId={dragOverFolderId}
              setDragOverFolderId={setDragOverFolderId}
              dropInsertHint={dropInsertHint}
              setDropInsertHint={setDropInsertHint}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function treeNodeMatchesSearch(node: HandbookTrainingTreeNode, q: string): boolean {
  if (!q) return true;
  if (handbookTrainingSearchMatch(node, q)) return true;
  return node.children.some(c => treeNodeMatchesSearch(c, q));
}

const HandbookTrainingTree: React.FC<Props> = ({
  tree,
  flatNodes,
  watchedNodeIds,
  selectedId,
  searchQuery,
  canManage = false,
  onSelect,
  onMoveNode,
  onInsertSibling,
}) => {
  const q = searchQuery.trim();
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [dropInsertHint, setDropInsertHint] = useState<HandbookTrainingDropInsertHint | null>(null);

  const flatVisible = useMemo(() => {
    if (!flatNodes?.length) return [];
    return flatNodes
      .filter(n => {
        if (!q) return true;
        return handbookTrainingSearchMatch(n, q);
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
  }, [flatNodes, q]);

  const onRootDragOver = (e: React.DragEvent) => {
    if (!canManage || !onMoveNode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverRoot(true);
  };

  const onRootDrop = (e: React.DragEvent) => {
    if (!canManage || !onMoveNode) return;
    e.preventDefault();
    setDragOverRoot(false);
    const draggedId = readDragNodeId(e);
    if (!draggedId) return;
    onMoveNode(draggedId, null);
  };

  if (!tree.length) {
    if (flatVisible.length > 0) {
      return (
        <div className="people-handbook-training-tree">
          {canManage && onMoveNode && (
            <div
              className={`people-handbook-training-drop-root ${dragOverRoot ? 'active' : ''}`}
              onDragOver={onRootDragOver}
              onDragLeave={() => setDragOverRoot(false)}
              onDrop={onRootDrop}
            >
              Drop here for top level
            </div>
          )}
          {flatVisible.map(node => (
            <button
              key={node.id}
              type="button"
              className={`people-handbook-training-tree-item ${node.kind} ${
                selectedId === node.id ? 'active' : ''
              }`}
              onClick={() => onSelect(node.id, node.kind)}
            >
              {node.kind === 'folder' ? (
                <Folder size={14} aria-hidden className="people-handbook-training-tree-icon" />
              ) : (
                <Video size={14} aria-hidden className="people-handbook-training-tree-icon" />
              )}
              <span className="people-handbook-training-tree-label">
                {node.title}
                {node.kind === 'video' && (
                  <VideoStatusBadges node={node} watchedNodeIds={watchedNodeIds} />
                )}
              </span>
            </button>
          ))}
        </div>
      );
    }
    return <p className="people-hub-muted people-handbook-training-tree-empty">No training content yet.</p>;
  }

  return (
    <div className="people-handbook-training-tree">
      {canManage && (onMoveNode || onInsertSibling) && (
        <>
          <p className="people-handbook-training-organize-hint">
            Drag the handle: drop on a row to reorder; lower part of a folder row to nest inside.
          </p>
          {onMoveNode && (
            <div
              className={`people-handbook-training-drop-root ${dragOverRoot ? 'active' : ''}`}
              onDragOver={onRootDragOver}
              onDragLeave={() => setDragOverRoot(false)}
              onDrop={onRootDrop}
            >
              Drop here for top level
            </div>
          )}
        </>
      )}
      {tree.map(node => (
        <TreeBranch
          key={node.id}
          node={node}
          watchedNodeIds={watchedNodeIds}
          selectedId={selectedId}
          searchQuery={searchQuery}
          canManage={canManage}
          onSelect={onSelect}
          onMoveNode={onMoveNode}
          onInsertSibling={onInsertSibling}
          defaultOpen={!q || subtreeContainsSelection(node, selectedId)}
          dragOverFolderId={dragOverFolderId}
          setDragOverFolderId={setDragOverFolderId}
          dropInsertHint={dropInsertHint}
          setDropInsertHint={setDropInsertHint}
        />
      ))}
    </div>
  );
};

export default HandbookTrainingTree;
